import { computed, ComputedRef } from "vue";
import { BuilderEstimatorSteps } from "@/enums/BuilderEstimatorSteps";
import { isValidNumberOrEmpty } from "@/helpers/isValidNumberOrEmpty";
import {
  IBuilderEstimatorFormData,
  ILandDetails,
  ISelectDesign,
} from "@/types/BuilderEstimatorFormData";

type StepValidation = {
  validate: () => boolean;
};

interface IBuilderEstimatorStepValidations {
  stepValidations: ComputedRef<{
    [key in BuilderEstimatorSteps]: StepValidation;
  }>;
}

export const useBuilderEstimatorStepValidations = (
  formData: IBuilderEstimatorFormData
): IBuilderEstimatorStepValidations => {
  const OPTIONAL_STEP_ALWAYS_VALID = true;

  const validateLandDetails = (landDetails: ILandDetails): boolean => {
    const isWidthValid = isValidNumberOrEmpty(landDetails.landWidth);
    const isDepthValid = isValidNumberOrEmpty(landDetails.landDepth);

    return isWidthValid && isDepthValid;
  };

  const validateSelectDesign = (selectDesign: ISelectDesign): boolean => {
    return !!selectDesign.houseBuilderId && !!selectDesign.houseDesignId;
  };

  const validateSelectFacade = (): boolean => {
    const isFacadeSelected =
      formData[BuilderEstimatorSteps.SELECT_FACADE].facade?._id;

    return !!isFacadeSelected;
  };

  const validateSelectExternalColours = (): boolean => {
    const isExternalColourSelected =
      formData[BuilderEstimatorSteps.SELECT_EXTERNAL_COLOURS]
        .externalColorScheme?._id;

    return !!isExternalColourSelected;
  };
  const validateSelectInternalColours = (): boolean => {
    const isInternalColourSelected =
      formData[BuilderEstimatorSteps.SELECT_INTERNAL_COLOURS]
        .internalColorScheme?._id;

    return !!isInternalColourSelected;
  };

  const validateSelectInclusions = (): boolean => {
    return OPTIONAL_STEP_ALWAYS_VALID;
  };

  const validateOptionalUpgrades = (): boolean => {
    return OPTIONAL_STEP_ALWAYS_VALID;
  };

  const validateSummary = (): boolean => {
    return true;
  };

  const stepValidations = computed(() => ({
    [BuilderEstimatorSteps.LAND_DETAILS]: {
      validate: () =>
        validateLandDetails(formData[BuilderEstimatorSteps.LAND_DETAILS]),
    },
    [BuilderEstimatorSteps.SELECT_DESIGN]: {
      validate: () =>
        validateSelectDesign(formData[BuilderEstimatorSteps.SELECT_DESIGN]),
    },
    [BuilderEstimatorSteps.SELECT_FACADE]: {
      validate: () => validateSelectFacade(),
    },
    [BuilderEstimatorSteps.SELECT_EXTERNAL_COLOURS]: {
      validate: () => validateSelectExternalColours(),
    },
    [BuilderEstimatorSteps.SELECT_INCLUSIONS]: {
      validate: () => validateSelectInclusions(),
    },
    [BuilderEstimatorSteps.OPTIONAL_UPGRADES]: {
      validate: () => validateOptionalUpgrades(),
    },
    [BuilderEstimatorSteps.SUMMARY]: {
      validate: () => validateSummary(),
    },
    [BuilderEstimatorSteps.SELECT_INTERNAL_COLOURS]: {
      validate: () => validateSelectInternalColours(),
    },
    [BuilderEstimatorSteps.VIEW_DESIGN]: {
      validate: () =>
        validateSelectDesign(formData[BuilderEstimatorSteps.SELECT_DESIGN]),
    },
  }));

  return {
    stepValidations,
  };
};
